import { AlertTriangle, Check, Siren } from "lucide-react"
import { toast } from "sonner"
import { useNavigate } from "@tanstack/react-router"
import {
  useNotificationsInbox,
  useAcknowledge,
  isSosEvent,
  isClientSosEvent,
  isPatrolOverdueEvent,
  readPatrolIdFromPayload,
  type NotificationListItem,
} from "@/entities/notification"
import { LOCATION_UNCONFIRMED_LABEL } from "@/entities/incident"
import { Button } from "@/shared/components/ui/button"

function dedupeByAlertId(items: NotificationListItem[]): NotificationListItem[] {
  const seen = new Set<number>()
  const result: NotificationListItem[] = []
  for (const item of items) {
    if (item.acknowledgeableAlertId == null) continue
    if (seen.has(item.acknowledgeableAlertId)) continue
    seen.add(item.acknowledgeableAlertId)
    result.push(item)
  }
  return result
}

const CLIENT_REPORTER_FALLBACK = "Client portal user"

type BannerIncidentInfo = { severity?: string; subtype?: string; siteName?: string; guardName?: string }

function readText(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key]
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined
}

function parsePayload(payload: string | null | undefined): Record<string, unknown> {
  if (!payload) return {}
  try {
    const parsed = JSON.parse(payload) as unknown
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

function readPatrolId(item: NotificationListItem): number | null {
  return readPatrolIdFromPayload(item.payload)
}

function canOpen(item: NotificationListItem): boolean {
  if (isPatrolOverdueEvent(item.event)) return readPatrolId(item) != null
  return item.sourceId != null
}

function parseIncidentInfo(item: NotificationListItem): BannerIncidentInfo {
  const p = parsePayload(item.payload)
  const severity = readText(p, "severity")
  const subtype = readText(p, "subtype")
  const siteName = readText(p, "siteName")
  const guardName = readText(p, "guardName")

  if (!isClientSosEvent(item.event)) return { severity, subtype, siteName, guardName }

  return {
    severity,
    subtype,
    siteName: siteName ?? LOCATION_UNCONFIRMED_LABEL,
    guardName: readText(p, "clientName") ?? CLIENT_REPORTER_FALLBACK,
  }
}

export function CriticalAckBanner() {
  const { data } = useNotificationsInbox(1, 50)
  const acknowledge = useAcknowledge()
  const navigate = useNavigate()

  const allItems = data?.items.items ?? []
  const pending = allItems.filter(
    (n) =>
      n.priority === "Critical" &&
      n.acknowledgeableAlertId != null &&
      n.alertStatus === "Pending",
  )
  const unacked = dedupeByAlertId(pending)

  if (unacked.length === 0) return null

  const latest = unacked[0]
  const moreCount = unacked.length - 1
  const sos = isSosEvent(latest.event)
  const info = parseIncidentInfo(latest)
  const detail = [info.siteName, info.guardName].filter(Boolean).join(" · ")

  const handleAcknowledge = () => {
    acknowledge.mutate(latest.acknowledgeableAlertId!, {
      onSuccess: () => toast.success("Acknowledged"),
      onError: () => toast.error("Failed to acknowledge alert"),
    })
  }

  const openable = canOpen(latest)

  const handleOpen = () => {
    if (isPatrolOverdueEvent(latest.event)) {
      const patrolId = readPatrolId(latest)
      if (patrolId == null) return
      navigate({ to: "/patrols/$patrolId", params: { patrolId: String(patrolId) } })
      return
    }
    if (latest.sourceId == null) return
    navigate({
      to: "/incidents/$incidentId",
      params: { incidentId: String(latest.sourceId) },
    })
  }

  if (sos) {
    return (
      <div className="flex items-center gap-3 border-b border-destructive/40 bg-destructive/10 px-4 py-2.5 text-destructive">
        <Siren size={16} strokeWidth={2} className="shrink-0 animate-pulse" />
        <span className="text-[11px] font-bold uppercase tracking-wider shrink-0">
          {isClientSosEvent(latest.event) ? "Client SOS" : "SOS Emergency"}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold">
            {latest.title}
            {moreCount > 0 && (
              <span className="ml-1.5 text-[12px] font-normal opacity-75">
                +{moreCount} more
              </span>
            )}
          </div>
          {detail && <div className="truncate text-[12px] font-medium opacity-90">{detail}</div>}
        </div>
        {openable && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleOpen}
            className="h-7 gap-1.5 border-destructive/40 bg-transparent px-2.5 text-[12px] text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            Open
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={handleAcknowledge}
          disabled={acknowledge.isPending}
          className="h-7 gap-1.5 border-destructive/40 bg-transparent px-2.5 text-[12px] text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <Check size={12} strokeWidth={2.2} />
          Acknowledge
        </Button>
      </div>
    )
  }

  const heading =
    info.severity && info.subtype
      ? `${info.severity} severity incident — ${info.subtype}`
      : latest.title

  return (
    <div className="flex items-center gap-3 border-b border-bad-bd bg-bad-bg px-4 py-2.5 text-bad">
      <AlertTriangle size={15} strokeWidth={2} className="shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[13px] font-semibold">
          <span className="truncate">{heading}</span>
          {moreCount > 0 && (
            <span className="shrink-0 text-[12px] font-normal opacity-75">+{moreCount} more</span>
          )}
        </div>
        {detail && <div className="truncate text-[12px] font-normal opacity-80">{detail}</div>}
      </div>
      {openable && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleOpen}
          className="h-7 gap-1.5 border-bad-bd bg-transparent px-2.5 text-[12px] text-bad hover:bg-bad/10 hover:text-bad"
        >
          Open
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={handleAcknowledge}
        disabled={acknowledge.isPending}
        className="h-7 gap-1.5 border-bad-bd bg-transparent px-2.5 text-[12px] text-bad hover:bg-bad/10 hover:text-bad"
      >
        <Check size={12} strokeWidth={2.2} />
        Acknowledge
      </Button>
    </div>
  )
}
